import {
  faCheck,
  faCircle,
  faRobot,
  faStar as faStarRegular,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCircleCheck,
  faCircleThreeQuartersStroke,
  faStar,
} from "@fortawesome/pro-solid-svg-icons";
import { Button, Dropdown, Icon, Tag, Tooltip } from "@intility/bifrost-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import type { ClientListItem, PlatformListItem } from "../../api/types";
import { GoToTag } from "../../components/GoToTag";
import { RoleBadge } from "../../components/RoleBadge";
import { clientPath } from "../../lib/clientPath";
import { FROM_MYHUB } from "../../lib/fromMyHub";
import s from "./MyPlatforms-PlatformCard.module.css";
import {
  isOnboardingComplete,
  ONBOARDING_STEP_DESCRIPTIONS,
  ONBOARDING_STEPS,
  onboardingSteps,
} from "./onboardingSteps";

function StatusBar({ steps }: { steps: boolean[] }) {
  if (isOnboardingComplete(steps)) {
    return (
      <div className={`${s.statusBar} ${s.complete}`}>
        <Icon icon={faCheck} />
        Onboard Complete
      </div>
    );
  }

  // The first step still open is the one in progress.
  const current = steps.findIndex((done) => !done);
  const doneCount = steps.filter(Boolean).length;

  return (
    <div
      aria-label={`${doneCount} of ${steps.length} steps done. Next: ${ONBOARDING_STEPS[current]}`}
      className={`${s.statusBar} ${s.steps}`}
      role="img"
    >
      {steps.map((done, i) => (
        <span
          key={ONBOARDING_STEPS[i]}
          style={{ alignItems: "center", display: "flex", gap: "inherit" }}
        >
          {i > 0 && (
            <span
              className={`${s.connector} ${done ? s.connectorDone : s.connectorTodo}`}
            />
          )}
          <Tooltip
            content={
              <>
                <strong>{ONBOARDING_STEPS[i]}</strong>
                <br />
                {ONBOARDING_STEP_DESCRIPTIONS[i]}
              </>
            }
            placement="top"
          >
            <span>
              {done ? (
                <Icon className={s.done} icon={faCircleCheck} />
              ) : i === current ? (
                <Icon className={s.todo} icon={faCircleThreeQuartersStroke} />
              ) : (
                <Icon className={s.todo} icon={faCircle} />
              )}
            </span>
          </Tooltip>
        </span>
      ))}
    </div>
  );
}

// With more clients than this, the last row becomes a "+N" tag.
const MAX_CLIENT_ROWS = 3;

function ClientTag({ client }: { client: ClientListItem }) {
  const navigate = useNavigate();
  return (
    <GoToTag
      kind="client"
      onGo={() => navigate(clientPath(client), { state: FROM_MYHUB })}
      style={{ maxWidth: "100%" }}
      title={client.name}
    >
      <span className={s.tagLabel}>
        <Icon icon={faRobot} marginRight />
        {client.name}
      </span>
    </GoToTag>
  );
}

function MoreClients({ clients }: { clients: ClientListItem[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Dropdown
      visible={open}
      onHide={() => setOpen(false)}
      placement="bottom-start"
      content={
        // The dropdown renders in a portal, but React still bubbles its clicks
        // to the card, which would open the platform.
        // biome-ignore lint/a11y/useKeyWithClickEvents: only stops propagation.
        // biome-ignore lint/a11y/noStaticElementInteractions: only stops propagation.
        <div className={s.moreClients} onClick={(e) => e.stopPropagation()}>
          {clients.map((client) => (
            <ClientTag client={client} key={client.id} />
          ))}
        </div>
      }
    >
      <Tag
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`${clients.length} more clients`}
        onClick={() => setOpen((v) => !v)}
      >
        +{clients.length}
      </Tag>
    </Dropdown>
  );
}

export default function PlatformCard({
  platform,
  clients,
  onToggleStar,
  starred,
}: {
  platform: PlatformListItem;
  clients: ClientListItem[];
  onToggleStar: () => void;
  starred: boolean;
}) {
  const steps = onboardingSteps(platform);
  const navigate = useNavigate();
  const overflow = clients.length > MAX_CLIENT_ROWS;
  const shown = overflow ? clients.slice(0, MAX_CLIENT_ROWS - 1) : clients;
  const hidden = overflow ? clients.slice(MAX_CLIENT_ROWS - 1) : [];

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the platform name inside is a real link, which is the keyboard route to the same page.
    // biome-ignore lint/a11y/noStaticElementInteractions: see above.
    <div
      className={s.card}
      onClick={(event) => {
        // Buttons and links in the card handle their own click.
        if (
          event.target instanceof Element &&
          event.target.closest("a, button")
        )
          return;
        navigate(`/platforms/${platform.id}`, { state: FROM_MYHUB });
      }}
    >
      <div className={s.top}>
        <span className={s.star}>
          <Button
            aria-label={`Favourite ${platform.name}`}
            aria-pressed={starred}
            noPadding
            onClick={onToggleStar}
            state="neutral"
            variant="flat"
          >
            <Icon
              className={starred ? "bfc-warning" : "bfc-base-2"}
              icon={starred ? faStar : faStarRegular}
            />
          </Button>
        </span>
        <div className={s.text}>
          <Link
            className={`bf-neutral-link ${s.nameLink}`}
            state={FROM_MYHUB}
            to={`/platforms/${platform.id}`}
          >
            <span className={s.name}>{platform.name}</span>
          </Link>
          {platform.description && (
            <span className={`${s.description} bfc-base-2`}>
              {platform.description}
            </span>
          )}
        </div>
        <span className={s.role}>
          <RoleBadge role={platform.userRole} />
        </span>
      </div>

      <StatusBar steps={steps} />

      {clients.length > 0 && (
        // Only the clients' tags, side by side.
        <div className={s.clients}>
          {shown.map((client) => (
            <ClientTag client={client} key={client.id} />
          ))}
          {hidden.length > 0 && <MoreClients clients={hidden} />}
        </div>
      )}
    </div>
  );
}
